import React, { useState, useRef, useEffect } from "react";
import { LAYOUT_VARIANTS } from "../config/appConfig";
import { SlidersHorizontal, X } from "lucide-react";

// Small hidden dev toggle so a reviewer can flip DUEL / SHOWDOWN and restart.
// Not part of the consumer chrome.

export default function DevToolbar({ variant, onVariant, onRestart }) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  // Standard dismissal: click/tap outside the popover closes it (clicks inside
  // are ignored via ref containment); Escape also closes. Listeners are only
  // active while open. Nothing else about the popover changes.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!open) {
    return (
      <button
        data-testid="dev-toggle-open"
        onClick={() => setOpen(true)}
        aria-label="Open options"
        className="fixed z-[80] bottom-3 left-3 w-8 h-8 rounded-full bg-white/6 border border-white/10 backdrop-blur flex items-center justify-center text-white/50 hover:text-white hover:bg-white/12 transition-colors"
      >
        <SlidersHorizontal size={14} />
      </button>
    );
  }

  return (
    <div ref={panelRef} className="fixed z-[80] bottom-3 left-3 rounded-2xl bg-black/85 border border-white/12 backdrop-blur-md p-3 w-52">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-silver">OPTIONS</span>
        <button data-testid="dev-toggle-close" onClick={() => setOpen(false)} className="text-white/40 hover:text-white">
          <X size={14} />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-1.5 mb-2">
        {[
          { id: LAYOUT_VARIANTS.DUEL, label: "DUEL" },
          { id: LAYOUT_VARIANTS.SHOWDOWN, label: "SHOWDOWN" },
        ].map((v) => (
          <button
            key={v.id}
            data-testid={`dev-variant-${v.id}`}
            onClick={() => onVariant(v.id)}
            className={`rounded-lg py-2 font-display font-bold text-[10px] tracking-[0.08em] uppercase transition-colors ${
              variant === v.id
                ? "bg-white text-black"
                : "bg-white/6 text-white/60 hover:bg-white/12"
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>
      <button
        data-testid="dev-restart"
        onClick={onRestart}
        className="w-full rounded-lg py-2 font-display font-bold text-[10px] tracking-[0.08em] uppercase bg-white/6 text-white/60 hover:bg-white/12 transition-colors"
      >
        Restart run
      </button>
    </div>
  );
}
